import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../app/src/store/store';
import { apiService } from './api';
import { willTokenExpireSoon, getTimeUntilExpiration } from './jwtUtils';

/**
 * Configuration for token refresh behavior
 */
const TOKEN_REFRESH_CONFIG = {
  // Check token expiration every 5 minutes
  CHECK_INTERVAL_MS: 5 * 60 * 1000, // 5 minutes

  // Refresh token if it will expire in the next 5 minutes
  REFRESH_THRESHOLD_MINUTES: 5,

  // Enable debug logging
  DEBUG: false,
};

/**
 * Hook to automatically refresh access token before it expires
 *
 * How it works:
 * 1. Checks every 5 minutes if the access token is about to expire
 * 2. If token will expire within the next 5 minutes, automatically refreshes it
 * 3. Only runs when user is authenticated
 * 4. Stops checking when user logs out
 */
export const useAutoTokenRefresh = () => {
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  const log = useCallback((...args: unknown[]) => {
    if (TOKEN_REFRESH_CONFIG.DEBUG) {
      console.log('[AutoTokenRefresh]', ...args);
    }
  }, []);

  /**
   * Check if token needs refresh and refresh if necessary
   */
  const checkAndRefreshToken = useCallback(async () => {
    // Skip if not authenticated or no user
    if (!isAuthenticated || !user) {
      log('Skipping refresh check - user not authenticated');
      return;
    }

    // Skip if already refreshing
    if (isRefreshingRef.current) {
      log('Skipping refresh check - already refreshing');
      return;
    }

    // Get access token from localStorage
    const accessToken =
      typeof window !== 'undefined'
        ? localStorage.getItem('accessToken')
        : null;

    if (!accessToken) {
      log('No access token found');
      return;
    }

    // Check if token will expire soon
    const willExpireSoon = willTokenExpireSoon(
      accessToken,
      TOKEN_REFRESH_CONFIG.REFRESH_THRESHOLD_MINUTES
    );

    if (willExpireSoon) {
      const timeUntilExpiration = getTimeUntilExpiration(accessToken);
      const minutesRemaining = Math.floor(timeUntilExpiration / (60 * 1000));

      log(
        `Token will expire in ${minutesRemaining} minutes. Refreshing now...`
      );

      try {
        isRefreshingRef.current = true;
        const success = await apiService.refreshAccessToken();

        if (success) {
          log('Token refreshed successfully');
        } else {
          log('Token refresh failed');
        }
      } catch (error) {
        console.error('Error during automatic token refresh:', error);
      } finally {
        isRefreshingRef.current = false;
      }
    } else {
      const timeUntilExpiration = getTimeUntilExpiration(accessToken);
      const minutesRemaining = Math.floor(timeUntilExpiration / (60 * 1000));
      log(
        `Token is valid for ${minutesRemaining} more minutes. No refresh needed.`
      );
    }
  }, [isAuthenticated, user, log]);

  /**
   * Set up the periodic token check
   */
  useEffect(() => {
    if (!isAuthenticated || !user) {
      // Clear interval if user logs out
      if (intervalRef.current) {
        log('Clearing token refresh interval - user logged out');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    log('Setting up automatic token refresh');

    // Check immediately when hook is mounted and user is authenticated
    checkAndRefreshToken();

    // Then check every 5 minutes
    intervalRef.current = setInterval(() => {
      checkAndRefreshToken();
    }, TOKEN_REFRESH_CONFIG.CHECK_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        log('Cleaning up token refresh interval');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, user, checkAndRefreshToken, log]);

  return {
    // Expose manual refresh function in case it's needed
    manualRefresh: checkAndRefreshToken,
  };
};
